import { Injectable } from '@angular/core';
import { EProductAbbridged } from '@gelato-api-ui/core/e-commerce/e-product-abbridged';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { AppState } from '@api-ui-app/src/app/app.state';
import { EProductStatus } from '@gelato-api-ui/core/e-commerce/e-product-status.enum';
import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { ShowGeneralErrorNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { TemporaryProductIdService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/temporary-product-id.service';
import * as eCommerceProductsActions from '@api-ui-app/src/app/ngrx/e-commerce-products.actions';
import { ECommerceProductImagesArrayService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-images-array.service';
import { EProductPublishingErrorCode } from '@gelato-api-ui/core/e-commerce/e-product-publishing-errors.enum';
import { getIsInternalStoreType, getStoreType } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import * as actions from '@api-ui-app/src/app/e-commerce-product-publishing/+state/e-commerce-product-publishing.actions';
import { getCurrentIso8601DateTime } from '@api-ui-app/src/app/lib/getCurrentIso8601DateTime';
import { ECommerceProductPublishingNotificationsFacade } from '@api-ui-app/src/app/e-commerce-product-publishing/e-commerce-product-publishing-notifications.facade';
import { getProductsList } from '@api-ui-app/src/app/ngrx/e-commerce-products.selector';
import * as selectors from '@api-ui-app/src/app/e-commerce-product-publishing/+state/e-commerce-product-publishing.selectors';
import { SECOND } from '@gelato-api-ui/utils/time';
import { ECommerceProductGraphQlService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-graph-ql.service';
import { ECommerceProductPublishingEventTrackingService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/e-commerce-product-publishing-event-tracking.service';
import { ECommerceProductPublishingService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/e-commerce-product-publishing.service';
import { MetadataItem } from '@gelato-api-ui/core/metadata/types/metadata-item';

@Injectable({ providedIn: 'root' })
export class ECommerceProductPublishingFacade {
  readonly storeType$ = this.store.select(getStoreType);
  readonly isInternalStoreType$ = this.store.select(getIsInternalStoreType);
  readonly products$ = this.store.select(getProductsList);

  private readonly isPublishingProcessMonitoringActive$ = this.store.select(
    selectors.getIsPublishingProcessMonitoringActive,
  );
  private readonly publishRepeatRequestTime = 5 * SECOND;

  constructor(
    private readonly store: Store<AppState>,
    private readonly notificationsFacade: ECommerceProductPublishingNotificationsFacade,
    private readonly temporaryProductIdService: TemporaryProductIdService,
    private readonly eCommerceProductImagesArrayService: ECommerceProductImagesArrayService,
    private readonly eCommerceProductGraphQlService: ECommerceProductGraphQlService,
    private readonly eCommerceProductPublishingEventTrackingService: ECommerceProductPublishingEventTrackingService,
    private readonly eCommerceProductPublishingService: ECommerceProductPublishingService,
  ) {}

  initiatePublishingProcessMonitoring(statuses: EProductStatus[] = [EProductStatus.PUBLISHING]) {
    this.store.dispatch(actions.initiatePublishingProcessMonitoring({ statuses }));
  }

  cancelPublishingProcessMonitoring() {
    this.store.dispatch(actions.cancelPublishingProcessMonitoring());
  }

  trackProductStatus(originalProduct: EProductAbbridged): Observable<void> {
    return combineLatest([timer(2000, this.publishRepeatRequestTime), this.isPublishingProcessMonitoringActive$]).pipe(
      takeWhile(([, isPublishingProcessMonitoringActive]) => isPublishingProcessMonitoringActive),
      mergeMap(() => this.getProductInfo(originalProduct.id)),
      tap(product => {
        if (product?.status === EProductStatus.PUBLISHING) {
          this.updateProductStatus(product.id, product.status);
        }
      }),
      filter(product => product && ![EProductStatus.PUBLISHING, EProductStatus.CREATED].includes(product.status)),
      take(1),
      tap(product => {
        if (product.status === EProductStatus.PUBLISHING_ERROR) {
          this.onPublishingError(product);
        } else {
          this.onPublishingSuccess(product);
        }
      }),
      map(() => undefined),
    );
  }

  handleMultipleProductsSavingStart(
    abridgedProducts: EProductAbbridged[],
    publishImmediately: boolean,
  ): EProductAbbridged[] {
    const products = abridgedProducts.map(loopProduct => ({
      ...loopProduct,
      id: loopProduct.id || this.temporaryProductIdService.generate(),
    }));

    this.addProducts(products);
    this.showProductSavingStartInfoMessage(publishImmediately);

    return products;
  }

  handleMultipleProductsSavingSuccess(abridgedProducts: EProductAbbridged[], resultProducts: EProductWithVariants[]) {
    const existingProductIds = abridgedProducts
      .map(loopProduct => loopProduct.id)
      .filter(id => !this.temporaryProductIdService.isTemporaryProductId(id));
    const temporaryProductIds = abridgedProducts
      .map(loopProduct => loopProduct.id)
      .filter(id => this.temporaryProductIdService.isTemporaryProductId(id));

    if (temporaryProductIds?.length) {
      this.removeProducts(temporaryProductIds);
    }

    const newProducts = resultProducts.filter(loopProduct => !existingProductIds.includes(loopProduct?.id));

    if (newProducts?.length) {
      this.addProducts(newProducts);
    }

    this.isInternalStoreType$
      .pipe(
        take(1),
        tap(isInternalStoreType => {
          resultProducts.forEach(loopProduct => {
            if (isInternalStoreType) {
              this.updateProductStatus(loopProduct.id, loopProduct.status);
            } else {
              this.publishProduct(loopProduct.id, loopProduct.metadata);
            }
          });
        }),
      )
      .subscribe();
  }

  handleMultipleProductsSavingFailure(abridgedProducts: EProductAbbridged[]) {
    this.store.dispatch(new ShowGeneralErrorNotification());

    const existingProducts = abridgedProducts.filter(
      loopProduct => !this.temporaryProductIdService.isTemporaryProductId(loopProduct.id),
    );
    const temporaryProductIds = abridgedProducts
      .map(loopProduct => loopProduct.id)
      .filter(id => this.temporaryProductIdService.isTemporaryProductId(id));

    if (existingProducts?.length) {
      existingProducts.forEach(loopProduct => {
        this.updateProductStatus(loopProduct.id, loopProduct.status || EProductStatus.CREATED);
      });
    }

    if (temporaryProductIds?.length) {
      this.removeProducts(temporaryProductIds);
    }
  }

  handleSingleProductSavingStart(product: EProductWithVariants, publishImmediately: boolean): EProductAbbridged {
    return this.handleMultipleProductsSavingStart([this.getAbridgedProduct(product, true)], publishImmediately)?.[0];
  }

  handleSingleProductSavingSuccess(originalProductAbridged: EProductAbbridged, savedProduct: EProductWithVariants) {
    this.handleMultipleProductsSavingSuccess([originalProductAbridged], [savedProduct]);
  }

  handleSingleProductSavingFailure(originalProductAbridged: EProductAbbridged) {
    this.handleMultipleProductsSavingFailure([originalProductAbridged]);
  }

  handleDuplicateProductSuccess(product: EProductAbbridged) {
    this.notificationsFacade.showInfoNotification({ textKey: 'txt_products_duplicate_notification' });
    this.addProducts([{ ...product, status: EProductStatus.PUBLISHING }]);
    this.initiatePublishingProcessMonitoring();
  }

  handleDuplicateProductFailure(productId: string, productTitle: string, errorDetails: string) {
    this.notificationsFacade.showErrorStatusMessage(
      productId,
      productTitle,
      EProductPublishingErrorCode.GENERAL_PRODUCT_DUPLICATION_ERROR,
      errorDetails,
    );
  }

  isProductPublishing(id: string, status: EProductStatus): boolean {
    if (status) {
      // If product just created or publishing
      const isJustCreated =
        (!id || this.temporaryProductIdService.isTemporaryProductId(id)) && status === EProductStatus.CREATED;

      return isJustCreated || status === EProductStatus.PUBLISHING;
    }

    return false;
  }

  hideAllStatusMessages() {
    this.notificationsFacade.hideAllStatusMessages();
  }

  retryPublishProduct(productId: string) {
    this.notificationsFacade.hideStatusMessage(productId);
    this.products$
      .pipe(
        map(products => (products || []).find(product => product.id === productId)),
        filter(productAbridged => Boolean(productAbridged)),
        take(1),
        tap(() => this.updateProductStatus(productId, EProductStatus.PUBLISHING)),
        switchMap(() =>
          this.getProductInfo(productId).pipe(
            take(1),
            tap(product => this.publishProduct(productId, product?.metadata)),
          ),
        ),
      )
      .subscribe();
  }

  private removeProducts(productIds: string[]) {
    this.store.dispatch(eCommerceProductsActions.removeProducts({ productIds }));
  }

  private getAbridgedProduct(
    product: EProductWithVariants,
    usePreviewUrlFromProductImages: boolean,
  ): EProductAbbridged {
    const previewUrl = this.eCommerceProductImagesArrayService.getPrimaryProductImageUrl(product.productImages);
    const previewFieldOverrides = usePreviewUrlFromProductImages
      ? { previewUrl, externalThumbnailUrl: previewUrl }
      : {};

    return {
      ...product,
      ...previewFieldOverrides,
      variants: product.variants.map(loopVariant => ({
        id: loopVariant.id,
        productId: loopVariant.productId,
        title: loopVariant.title,
        connectionStatus: loopVariant.connectionStatus,
      })),
    };
  }

  private addProducts(sourceProducts: EProductAbbridged[]) {
    const products = sourceProducts.map(loopProduct => ({
      ...loopProduct,
      status: EProductStatus.PUBLISHING,
      updatedAt: getCurrentIso8601DateTime(),
    }));

    this.store.dispatch(eCommerceProductsActions.addProducts({ products }));
  }

  private updateProduct(id: string, payload: Partial<EProductAbbridged>, forcePreviewUpdate?: boolean) {
    this.store.dispatch(eCommerceProductsActions.updateProduct({ id, payload, forcePreviewUpdate }));
  }

  private updateProductStatus(id: string, status: EProductStatus) {
    this.updateProduct(id, { status, updatedAt: getCurrentIso8601DateTime() });
  }

  private showProductSavingStartInfoMessage(publishImmediately?: boolean) {
    this.store.dispatch(actions.showProductSavingStartInfoMessage({ publishImmediately }));
  }

  private publishProduct(productId: string, productMetadata: MetadataItem[]) {
    this.updateProductStatus(productId, EProductStatus.PUBLISHING);
    this.eCommerceProductPublishingService
      .publishProduct(productId, productMetadata)
      .pipe(
        take(1),
        filter(Boolean),
        tap(() => {
          this.initiatePublishingProcessMonitoring();
        }),
      )
      .subscribe();
  }

  private onPublishingSuccess(product: EProductWithVariants) {
    this.updateProductStatus(product.id, product.status);
    this.notificationsFacade.showSuccessStatusMessage(product);
    this.eCommerceProductPublishingEventTrackingService.trackPublishSuccess(product);
  }

  private onPublishingError(product: EProductWithVariants) {
    this.notificationsFacade.showErrorStatusMessage(
      product.id,
      product.title,
      product.publishingErrorCode,
      product.publishingErrorDetails,
    );
    this.updateProductStatus(product.id, product.status);
    this.eCommerceProductPublishingEventTrackingService.trackPublishError(product.id, product.publishingErrorCode);
  }

  private getProductInfo(productId: string): Observable<EProductWithVariants> {
    return this.eCommerceProductGraphQlService.getProduct(productId).pipe(
      tap(product => {
        this.updateProduct(productId, {
          externalThumbnailUrl: this.eCommerceProductImagesArrayService.getPrimaryProductImageUrl(
            product.productImages,
          ),
          variants: product.variants,
          hasDraft: product.hasDraft,
        });
      }),
      catchError(() => of(null)),
    );
  }
}
